import { useCallback, useEffect, useState } from 'react'
import { Cpu, X, Loader2, SquarePen, Trash2, Building2 } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { usePlatformFilterStore } from '../../../store/platformFilterStore'
import { platformDevicesService } from '../../../services/platformDevicesService'
import type { Device, DeviceLanguage } from '../../../types/api.types'
import { DEVICE_LANGUAGE_OPTIONS, DEFAULT_DEVICE_LANGUAGE, deviceLanguageBadge } from '../../../constants/deviceLanguage'
import { LoadErrorState } from '../../../components/LoadErrorState'
import { EmptyState } from '../../admin/components/EmptyState'
import { Tooltip } from '../../../components/Tooltip'

const STATUS_BADGE: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  BLOCKED: 'bg-red-100 text-red-700',
  RETIRED: 'bg-gray-100 text-gray-500',
}
const MODE_BADGE: Record<string, string> = {
  ai: 'bg-purple-100 text-purple-700',
  udp: 'bg-blue-100 text-blue-700',
}

interface DeviceForm {
  mac_address: string; communication_mode: 'ai' | 'udp'; language: DeviceLanguage
}
const EMPTY: DeviceForm = { mac_address: '', communication_mode: 'ai', language: DEFAULT_DEVICE_LANGUAGE }

export function PlatformDevicesPage(): JSX.Element {
  const { organizations, allProperties, selectedOrgId, selectedPropertyId, selectOrg } = usePlatformFilterStore()

  const [devices, setDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<DeviceForm>(EMPTY)
  const [submitting, setSubmitting] = useState(false)

  const loadData = useCallback(async () => {
    if (!selectedOrgId) { setDevices([]); return }
    setLoading(true); setLoadError(false)
    try {
      setDevices(await platformDevicesService.getByOrg(selectedOrgId, selectedPropertyId ?? undefined))
    }
    catch { setLoadError(true) }
    finally { setLoading(false) }
  }, [selectedOrgId, selectedPropertyId])

  useEffect(() => { loadData() }, [loadData])

  async function handleSubmit() {
    if (!selectedOrgId) return
    setSubmitting(true)
    try {
      const propId = selectedPropertyId ?? allProperties.find((p) => p.organization_id === selectedOrgId)?.id
      const payload = {
        mac_address: form.mac_address.trim() || '',
        communication_mode: form.communication_mode,
        language: form.language,
        property_id: propId ?? 0,
      }
      if (editingId) {
        await platformDevicesService.update(selectedOrgId, editingId, { communication_mode: form.communication_mode, language: form.language })
        toast.success('Device updated')
      } else {
        await platformDevicesService.create(selectedOrgId, payload)
        toast.success('Device registered')
      }
      setShowModal(false); setEditingId(null); setForm(EMPTY)
      await loadData()
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Save failed')
    } finally { setSubmitting(false) }
  }

  async function handleDelete(device: Device) {
    if (!selectedOrgId || !window.confirm('Delete this device? Cannot be undone.')) return
    try {
      await platformDevicesService.delete(selectedOrgId, device.id)
      toast.success('Device deleted')
      await loadData()
    } catch { toast.error('Delete failed') }
  }

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId)

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Devices &amp; Hardware</h1>
        <p className="mt-1 text-sm text-gray-500">Select an organisation to manage its devices. Optionally filter by property.</p>
      </div>

      {!selectedOrgId ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 py-20 text-center">
          <Building2 size={32} className="mb-3 text-gray-300" />
          <p className="text-[14px] font-medium text-gray-500">No organisation selected</p>
          <p className="mt-1 text-[13px] text-gray-400">Use the Organisation dropdown in the top bar.</p>
          {organizations.length > 0 && (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {organizations.slice(0, 5).map((o) => (
                <button key={o.id} type="button" onClick={() => selectOrg(o.id)}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-[13px] text-gray-600 hover:border-brand-green hover:text-brand-green">
                  {o.name}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[13px] text-gray-500">
              Devices for <span className="font-medium text-gray-900">{selectedOrg?.name ?? `Org #${selectedOrgId}`}</span>
              {selectedPropertyId && <span className="text-brand-green"> · filtered by property</span>}
            </p>
            <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); setShowModal(true) }}
              className="rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black hover:opacity-70">
              + Add Device
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={22} className="animate-spin text-brand-green" />
              <span className="ml-2 text-sm text-gray-500">Loading devices…</span>
            </div>
          ) : loadError ? (
            <LoadErrorState message="Couldn't load devices" onRetry={loadData} />
          ) : devices.length === 0 ? (
            <EmptyState icon={Cpu} message="No devices found" hint={selectedPropertyId ? 'Try clearing the property filter' : undefined} />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-5 py-3 text-left text-[12px] font-medium text-gray-500">ID</th>
                    <th className="px-5 py-3 text-left text-[12px] font-medium text-gray-500">Team / Device</th>
                    <th className="px-5 py-3 text-center text-[12px] font-medium text-gray-500">Mode</th>
                    <th className="px-5 py-3 text-center text-[12px] font-medium text-gray-500">Language</th>
                    <th className="px-5 py-3 text-center text-[12px] font-medium text-gray-500">Status</th>
                    <th className="px-5 py-3 text-left text-[12px] font-medium text-gray-500">Last Seen</th>
                    <th className="px-5 py-3 text-center text-[12px] font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => (
                    <tr key={d.id} className="border-t border-gray-100 hover:bg-gray-50/40">
                      <td className="px-5 py-3.5 text-[13px] text-gray-500">#{d.id}</td>
                      <td className="px-5 py-3.5">
                        {d.team_name
                          ? <p className="text-[13px] font-medium text-gray-900">{d.team_name}</p>
                          : <p className="text-[13px] text-gray-400">No team</p>
                        }
                        <p className="font-mono text-[11px] text-gray-400">{d.mac_address}</p>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium uppercase ${MODE_BADGE[d.communication_mode ?? ''] ?? 'bg-gray-100 text-gray-500'}`}>
                          {d.communication_mode ?? '—'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-600">
                          {deviceLanguageBadge(d.language)}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${STATUS_BADGE[d.status] ?? 'bg-gray-100 text-gray-500'}`}>
                          {d.status}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-[12px] text-gray-500">
                        {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-center gap-3">
                          <Tooltip label="Edit device">
                            <button type="button" onClick={() => {
                              setEditingId(d.id)
                              setForm({ mac_address: d.mac_address, communication_mode: (d.communication_mode ?? 'ai') as 'ai' | 'udp', language: d.language ?? DEFAULT_DEVICE_LANGUAGE })
                              setShowModal(true)
                            }} className="text-brand-green hover:text-brand-green/70"><SquarePen size={15} /></button>
                          </Tooltip>
                          <Tooltip label="Delete device">
                            <button type="button" onClick={() => handleDelete(d)} className="text-red-400 hover:text-red-600">
                              <Trash2 size={15} />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="max-h-[90dvh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-4 sm:p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-[15px] font-bold text-gray-900">{editingId ? 'Edit Device' : 'Register Device'}</h2>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              {!editingId && (
                <div>
                  <label className="mb-1 block text-[13px] font-medium text-gray-700">MAC Address <span className="text-gray-400">(optional — auto-generated if blank)</span></label>
                  <input value={form.mac_address} onChange={(e) => setForm({ ...form, mac_address: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 font-mono text-[14px] outline-none focus:border-brand-green"
                    placeholder="AA:BB:CC:DD:EE:FF" />
                </div>
              )}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Communication Mode</label>
                <select value={form.communication_mode} onChange={(e) => setForm({ ...form, communication_mode: e.target.value as 'ai' | 'udp' })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] outline-none focus:border-brand-green">
                  <option value="ai">AI</option>
                  <option value="udp">UDP</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Language</label>
                <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value as DeviceLanguage })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] outline-none focus:border-brand-green">
                  {DEVICE_LANGUAGE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={handleSubmit} disabled={submitting}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black hover:opacity-90 disabled:opacity-50">
                {submitting ? 'Saving…' : editingId ? 'Update' : 'Register'}
              </button>
              <button type="button" onClick={() => setShowModal(false)}
                className="flex-1 rounded-lg border border-gray-200 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
